import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useDebounce } from '@/hooks/useDebounce'
import { extractFirstUrl, normalizeUrl } from '@/lib/url'
import { fetchLinkCardMetadata } from '@/services/linkCardService'
import type { Protocol } from '@/contexts/ProtocolContext/ProtocolContext'
import type { LinkCardMetadata } from '@/types/linkCard'

interface UseLinkCardOptions {
    postText: string
    selectedProtocol: Protocol
    hasImages: boolean
}

/**
 * Hydration state for a link card restored from a Bluesky draft.
 *
 * `app.bsky.draft.defs#draftEmbedExternal` stores a bare `uri` — no title, description, or
 * thumbnail — so restoring a draft means re-fetching the metadata over the network. That fetch
 * can fail, or resolve somewhere else entirely (shortener expansion, a login wall, a domain
 * change), and silently publishing without the intended card or with a substituted one is a
 * content change the user never approved. Both cases require an explicit choice.
 */
export interface DraftLinkHydration {
    /** The URL as stored in the draft. */
    url: string
    status: 'pending' | 'error' | 'mismatch'
    /** The materially different URL the fetch resolved to, for `mismatch`. */
    resolvedUrl: string | null
}

export function useLinkCard({ postText, selectedProtocol, hasImages }: UseLinkCardOptions) {
    const queryClient = useQueryClient()
    const [dismissedUrl, setDismissedUrl] = useState<string | null>(null)
    // The "attached" URL persists even after the user deletes the URL from text
    const [attachedUrl, setAttachedUrl] = useState<string | null>(null)
    // URL restored from a draft that has not yet settled or been explicitly resolved by the user
    const [draftRestoreUrl, setDraftRestoreUrl] = useState<string | null>(null)
    const [draftRestoreAck, setDraftRestoreAck] = useState(false)
    const prevDebouncedUrlRef = useRef<string | null>(null)

    const rawUrl = extractFirstUrl(postText)
    const normalizedUrl = rawUrl ? normalizeUrl(rawUrl) : null
    const debouncedUrl = useDebounce(normalizedUrl, 500)

    // No link cards for Mastodon or when images are attached
    const isActive = selectedProtocol === 'bluesky' && !hasImages

    // The URL to query: use debouncedUrl when present, fall back to attachedUrl
    const queryUrl = debouncedUrl ?? attachedUrl

    // When a new URL appears in text, update attachedUrl and clear dismissal if it's different
    useEffect(() => {
        if (debouncedUrl && debouncedUrl !== prevDebouncedUrlRef.current) {
            setAttachedUrl(debouncedUrl)
            if (debouncedUrl !== dismissedUrl) {
                // New URL typed — reset dismissal
                setDismissedUrl(null)
            }
        }
        prevDebouncedUrlRef.current = debouncedUrl
    }, [debouncedUrl, dismissedUrl])

    const isDismissed = queryUrl === dismissedUrl

    const {
        data: metadata,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['linkCard', queryUrl],
        queryFn: ({ signal }) => fetchLinkCardMetadata(queryUrl!, signal),
        enabled: isActive && !!queryUrl && !isDismissed,
        staleTime: 10 * 60 * 1000,
        retry: false,
    })

    const dismissLinkCard = useCallback(() => {
        if (queryUrl) {
            setDismissedUrl(queryUrl)
        }
    }, [queryUrl])

    const resetLinkCard = useCallback(() => {
        setDismissedUrl(null)
        setAttachedUrl(null)
        setDraftRestoreUrl(null)
        setDraftRestoreAck(false)
        queryClient.removeQueries({ queryKey: ['linkCard'] })
    }, [queryClient])

    /**
     * Restore a link card from a saved draft.
     *
     * `metadata` is optional: Bluesky drafts store only the bare URL, so omitting it starts a
     * network fetch whose outcome the caller must resolve via `draftLinkHydration`.
     */
    const prefetchFromDraft = useCallback(
        (url: string, metadata?: LinkCardMetadata) => {
            if (metadata) {
                queryClient.setQueryData(['linkCard', url], metadata)
            }
            setAttachedUrl(url)
            setDraftRestoreUrl(url)
            // Nothing to hydrate when the metadata came with the draft.
            setDraftRestoreAck(!!metadata)
            if (dismissedUrl === url) {
                setDismissedUrl(null)
            }
        },
        [queryClient, dismissedUrl]
    )

    let draftLinkHydration: DraftLinkHydration | null = null
    if (draftRestoreUrl && !draftRestoreAck && queryUrl === draftRestoreUrl && !isDismissed) {
        const resolvedUrl = (metadata as LinkCardMetadata | undefined)?.url ?? null
        if (isLoading) {
            draftLinkHydration = { url: draftRestoreUrl, status: 'pending', resolvedUrl: null }
        } else if (isError || !metadata) {
            draftLinkHydration = { url: draftRestoreUrl, status: 'error', resolvedUrl: null }
        } else if (resolvedUrl && normalizeUrl(resolvedUrl) !== normalizeUrl(draftRestoreUrl)) {
            draftLinkHydration = { url: draftRestoreUrl, status: 'mismatch', resolvedUrl }
        }
    }

    /** Keep the card the fetch actually resolved to. Persisted on the next save, not silently. */
    const acceptResolvedDraftLink = useCallback(() => {
        setDraftRestoreAck(true)
    }, [])

    /** Drop the card entirely; the URL stays in the text exactly as the user wrote it. */
    const dismissDraftLink = useCallback(() => {
        if (draftRestoreUrl) setDismissedUrl(draftRestoreUrl)
        setDraftRestoreAck(true)
    }, [draftRestoreUrl])

    // Visible when active, has a query URL, and not dismissed
    const visibleCard =
        isActive && queryUrl && !isDismissed
            ? {
                  url: queryUrl,
                  metadata: (metadata as LinkCardMetadata) ?? null,
                  isLoading,
                  error: null,
                  isDismissed: false,
              }
            : null

    return {
        linkCard: visibleCard,
        dismissLinkCard,
        resetLinkCard,
        prefetchFromDraft,
        draftLinkHydration,
        acceptResolvedDraftLink,
        dismissDraftLink,
    }
}
